import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { Toast } from '../../types';

interface UIState {
  sidebarOpen: boolean;
  activeModal: string | null;
  modalData: unknown;
  toasts: Toast[];
  loadingStates: Record<string, boolean>;
  selectedTab: string;
  showMapView: boolean;
  selectedRequestId: string | null;
}

const initialState: UIState = {
  sidebarOpen: false,
  activeModal: null,
  modalData: null,
  toasts: [],
  loadingStates: {},
  selectedTab: 'dashboard',
  showMapView: false,
  selectedRequestId: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    openModal: (state, action: PayloadAction<{ type: string; data?: unknown }>) => {
      state.activeModal = action.payload.type;
      state.modalData = action.payload.data || null;
    },
    closeModal: (state) => {
      state.activeModal = null;
      state.modalData = null;
    },
    showToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
      state.toasts.push({
        id: Date.now().toString(),
        duration: 5000,
        ...action.payload,
      });
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    setLoadingState: (state, action: PayloadAction<{ key: string; loading: boolean }>) => {
      if (action.payload.loading) {
        state.loadingStates[action.payload.key] = true;
      } else {
        delete state.loadingStates[action.payload.key];
      }
    },
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload;
    },
    toggleMapView: (state) => {
      state.showMapView = !state.showMapView;
    },
    selectRequest: (state, action: PayloadAction<string>) => {
      state.selectedRequestId = action.payload;
    },
    setActiveModal: (state, action: PayloadAction<string | null>) => {
      state.activeModal = action.payload;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  openModal,
  closeModal,
  showToast,
  removeToast,
  setLoadingState,
  setSelectedTab,
  toggleMapView,
  selectRequest,
  setActiveModal,
} = uiSlice.actions;

export default uiSlice.reducer;
